/** Thin fetch wrapper. Auth travels in the httponly cookie set by /api/auth/login. */

import type {
  AgentActionCard,
  ChatMessage,
  Checklist,
  ChecklistItem,
  ClarifyDecision,
  Horizon,
  HorizonDetail,
  HorizonInput,
  InboxItem,
  Page,
  Project,
  ProjectCreateInput,
  ProjectInput,
  ProjectView,
  Reference,
  ReferenceFilters,
  ReferenceInput,
  Space,
  Stuff,
  StuffSource,
  Task,
  TaskCompletion,
  TaskFilters,
  TaskInput,
} from "./lists/types";
import { enqueue } from "./offline/queue";
import type { ReviewState } from "./review/types";
import type {
  BackupResult,
  BackupStatus,
  CalendarAgenda,
  CalendarStatus,
  CalendarSyncResult,
  Ceremony,
  CeremonyRun,
  EmailPollResult,
  EmailStatus,
  GoogleCalendar,
  LlmUsage,
  MemoryProfile,
  PushConfig,
  Setting,
  ToolMode,
} from "./settings/types";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const resp = await fetch(`/api${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
    ...init,
  });

  if (!resp.ok) {
    let detail = resp.statusText;
    try {
      const body = (await resp.json()) as { detail?: string };
      if (body.detail) detail = body.detail;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(resp.status, detail);
  }

  if (resp.status === 204) return undefined as T;
  return (await resp.json()) as T;
}

export interface HealthResponse {
  status: string;
  version: string;
  env: string;
  database: { ok: boolean; error: string | null };
}

type QueryValue = string | number | boolean | string[] | undefined | null;

function qs(params: Record<string, QueryValue>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    if (Array.isArray(value)) value.forEach((v) => search.append(key, v));
    else search.append(key, String(value));
  }
  const str = search.toString();
  return str ? `?${str}` : "";
}

const post = <T,>(path: string, body?: unknown) =>
  request<T>(path, { method: "POST", body: body === undefined ? undefined : JSON.stringify(body) });
const patch = <T,>(path: string, body: unknown) =>
  request<T>(path, { method: "PATCH", body: JSON.stringify(body) });
const put = <T,>(path: string, body: unknown) =>
  request<T>(path, { method: "PUT", body: JSON.stringify(body) });
const del = <T,>(path: string) => request<T>(path, { method: "DELETE" });

// --------------------------------------------------------------------------
// Capture z kolejką offline (Faza 7)
// --------------------------------------------------------------------------

/** Wynik wrzutki: albo zapisana w bazie, albo zakolejkowana do wysyłki. */
export interface CaptureResult {
  stuff: Stuff | null;
  queued: boolean;
}

/**
 * `POST /api/items`, który nie gubi wrzutki przy braku sieci.
 *
 * Ze sprawnym service workerem to on przechwytuje nieudany request i oddaje
 * 202 `{queued:true}`. Gdy SW nie kontroluje strony (dev server, pierwsze
 * wejście), fetch po prostu rzuca - wtedy kolejkujemy tutaj.
 */
async function captureWithQueue(
  raw_text: string,
  source: StuffSource = "chat",
  extra?: Record<string, unknown>,
): Promise<CaptureResult> {
  const body = JSON.stringify({ raw_text, source, extra: extra ?? null });
  let resp: Response;
  try {
    resp = await fetch("/api/items", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body,
    });
  } catch {
    await enqueue({ raw_text, source, extra: extra ?? null });
    return { stuff: null, queued: true };
  }

  if (resp.status === 202) {
    const queued = (await resp.json().catch(() => ({}))) as { queued?: boolean };
    if (queued.queued) return { stuff: null, queued: true };
  }
  if (!resp.ok) {
    let detail = resp.statusText;
    try {
      const errorBody = (await resp.json()) as { detail?: string };
      if (errorBody.detail) detail = errorBody.detail;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(resp.status, detail);
  }
  return { stuff: (await resp.json()) as Stuff, queued: false };
}

export const api = {
  health: () => request<HealthResponse>("/health"),
  me: () => request<{ authenticated: boolean }>("/auth/me"),
  login: (token: string) =>
    request<{ ok: boolean }>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ token }),
    }),
  logout: () => request<{ ok: boolean }>("/auth/logout", { method: "POST" }),

  // --- Phase 2: domain ---
  spaces: () => request<Space[]>("/spaces"),

  inbox: (params: { limit?: number; offset?: number } = {}) =>
    request<Page<InboxItem>>(`/items${qs(params)}`),
  capture: captureWithQueue,
  clarify: (stuffId: string, decision: ClarifyDecision) =>
    post<{ stuff: Stuff; task: Task | null; project: Project | null }>(
      `/items/${stuffId}/clarify`,
      decision,
    ),
  trashStuff: (stuffId: string) => del<Stuff>(`/items/${stuffId}`),

  tasks: (filters: TaskFilters = {}) =>
    request<Page<Task>>(`/tasks${qs(filters as Record<string, QueryValue>)}`),
  createTask: (input: TaskInput) => post<Task>("/tasks", input),
  updateTask: (id: string, input: Partial<TaskInput>) => patch<Task>(`/tasks/${id}`, input),
  completeTask: (id: string) => post<TaskCompletion>(`/tasks/${id}/complete`),
  uncompleteTask: (id: string) => post<Task>(`/tasks/${id}/uncomplete`),
  trashTask: (id: string) => del<Task>(`/tasks/${id}`),

  projects: (params: { space?: string; only_stalled?: boolean; status?: string[] } = {}) =>
    request<ProjectView[]>(`/projects${qs(params as Record<string, QueryValue>)}`),
  createProject: (input: ProjectCreateInput) =>
    post<{ project: Project; first_action: Task | null }>("/projects", input),
  projectTasks: (id: string) => request<Page<Task>>(`/projects/${id}/tasks?limit=200`),
  addProjectTask: (id: string, input: TaskInput) => post<Task>(`/projects/${id}/tasks`, input),
  updateProject: (id: string, input: ProjectInput) => patch<Project>(`/projects/${id}`, input),
  completeProject: (id: string) =>
    post<{ project: Project; trashed_tasks: Task[] }>(`/projects/${id}/complete`),
  trashProject: (id: string) => del<{ project: Project; trashed_tasks: Task[] }>(`/projects/${id}`),

  checklists: () => request<Checklist[]>("/checklists"),
  createChecklist: (name: string) => post<Checklist>("/checklists", { name }),
  deleteChecklist: (id: string) => del<void>(`/checklists/${id}`),
  addChecklistItem: (checklistId: string, text: string) =>
    post<ChecklistItem>(`/checklists/${checklistId}/items`, { text }),
  setChecklistItem: (checklistId: string, itemId: string, patchBody: { checked?: boolean; text?: string }) =>
    patch<ChecklistItem>(`/checklists/${checklistId}/items/${itemId}`, patchBody),
  removeChecklistItem: (checklistId: string, itemId: string) =>
    del<void>(`/checklists/${checklistId}/items/${itemId}`),
  clearChecked: (checklistId: string) =>
    post<{ removed: number }>(`/checklists/${checklistId}/clear-checked`),

  references: (filters: ReferenceFilters = {}) =>
    request<Page<Reference>>(`/references${qs(filters as Record<string, QueryValue>)}`),
  createReference: (input: ReferenceInput) => post<Reference>("/references", input),
  updateReference: (id: string, input: ReferenceInput) =>
    patch<Reference>(`/references/${id}`, input),
  trashReference: (id: string) => del<Reference>(`/references/${id}`),
  restoreReference: (id: string) => post<Reference>(`/references/${id}/restore`),

  horizons: (params: { space?: string; status?: string[] } = {}) =>
    request<Horizon[]>(`/horizons${qs(params as Record<string, QueryValue>)}`),
  horizon: (id: string) => request<HorizonDetail>(`/horizons/${id}`),
  createHorizon: (input: HorizonInput) => post<Horizon>("/horizons", input),
  updateHorizon: (id: string, input: HorizonInput) => patch<Horizon>(`/horizons/${id}`, input),

  // --- Faza 3: chat / agent ---
  chatMessages: (params: { limit?: number; before?: string } = {}) =>
    request<ChatMessage[]>(`/chat/messages${qs(params)}`),
  agentActions: (params: { limit?: number; pending_only?: boolean } = {}) =>
    request<AgentActionCard[]>(`/chat/actions${qs(params)}`),
  // Bez przestrzeni: backend wykonuje akcję w przestrzeni z momentu propozycji
  // (zapisanej w agent_actions), a nie w tej wybranej właśnie w UI.
  confirmAction: (id: string) => post<AgentActionCard>(`/chat/actions/${id}/confirm`),
  rejectAction: (id: string) => post<AgentActionCard>(`/chat/actions/${id}/reject`),
  undoAction: (id: string) => post<AgentActionCard>(`/chat/actions/${id}/undo`),
  toolModes: () => request<Record<string, ToolMode>>("/chat/tool-modes"),
  setToolModes: (modes: Record<string, ToolMode>) =>
    put<Record<string, ToolMode>>("/chat/tool-modes", { modes }),

  // --- Faza 4: ceremonie, ustawienia, pamięć, push ---
  ceremonies: () => request<Ceremony[]>("/ceremonies"),
  runCeremony: (name: string) => post<CeremonyRun>(`/ceremonies/${name}/run`),

  settings: () => request<Setting[]>("/settings"),
  saveSettings: (values: Record<string, unknown>) => put<Setting[]>("/settings", { values }),

  memory: () => request<MemoryProfile>("/memory"),
  saveMemory: (content: string) => put<MemoryProfile>("/memory", { content }),

  pushConfig: () => request<PushConfig>("/push/config"),
  pushSubscribe: (body: { endpoint: string; keys: { p256dh: string; auth: string } }) =>
    post<{ id: string }>("/push/subscriptions", body),
  pushUnsubscribe: (endpoint: string) => del<void>(`/push/subscriptions${qs({ endpoint })}`),
  pushTest: () => post<{ sent: number; skipped_reason: string | null }>("/push/test"),

  // --- Faza 5: Google Calendar ---
  calendarStatus: () => request<CalendarStatus>("/calendar/status"),
  /** Lista kalendarzy konta - wymaga połączenia (inaczej 409). */
  googleCalendars: () => request<GoogleCalendar[]>("/calendar/calendars"),
  calendarConfig: (body: {
    calendars?: string[];
    space_map?: Record<string, string | null>;
    write_calendar?: string;
  }) => put<CalendarStatus>("/calendar/config", body),
  calendarSync: () => post<CalendarSyncResult>("/calendar/sync"),
  calendarDisconnect: () => del<{ disconnected: boolean }>("/calendar/connection"),
  calendarEvents: (params: { date_from?: string; date_to?: string; space?: string } = {}) =>
    request<CalendarAgenda>(`/calendar/events${qs(params)}`),

  // --- Faza 7: capture z e-maila ---
  emailStatus: () => request<EmailStatus>("/email/status"),
  /** Ręczny poll skrzynki (ignoruje EMAIL_CAPTURE_ENABLED). */
  emailPoll: () => post<EmailPollResult>("/email/poll"),

  // --- Faza 8: eksport, backup, zużycie LLM ---
  backups: () => request<BackupStatus>("/export/backups"),
  runBackup: () => post<BackupResult>("/export/backups"),
  llmUsage: (days = 7) => request<LlmUsage>(`/llm/usage${qs({ days })}`),

  // --- Faza 6: przegląd tygodniowy ---
  review: () => request<ReviewState>("/review/current"),
  /** Start wysyła pierwszą wiadomość agenta po WS; tu wraca też jej tekst. */
  startReview: (space?: string | null) =>
    post<ReviewState>("/review/start", { space: space ?? null }),
  abandonReview: () => post<ReviewState>("/review/abandon"),
};

/** Start OAuth to pełne przeładowanie (redirect do Google), nie fetch. */
export const CALENDAR_OAUTH_START = "/api/calendar/oauth/start";

/**
 * Eksport pobieramy nawigacją, nie fetchem: przeglądarka sama zapisze plik
 * z nazwą z nagłówka Content-Disposition (ciasteczko sesji jedzie z żądaniem).
 */
export const EXPORT_DOWNLOAD = "/api/export";
